import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Dialog,
  Field,
  Input,
  Metric,
  Table,
  viLabel,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatePipeline } from '@/components/review/StatePipeline';
import { AuditTimeline } from '@/components/review/AuditTimeline';
import { useViewport } from '@/lib/viewport';
import { useScope } from '@/lib/scope';
import { useAuth } from '@/lib/auth';
import { useAdjustments } from '@/hooks/useAdjustments';
import { useMockTable } from '@/hooks/useMockTable';
import { reviewService } from '@/services/reviewService';
import { computeCutoff } from '@/engine/cutoff';
import type {
  AdjustmentState,
  AuditLog,
  Customer,
  ForecastAdjustment,
  ReviewDecision,
} from '@/domain';
import { formatNumber, toISODate } from '@/lib/utils';

type StateFilter = 'all' | AdjustmentState;

const STATE_TABS: { id: StateFilter; label: string }[] = [
  { id: 'all', label: 'Tất cả' },
  { id: 'NEW', label: 'Chờ duyệt' },
  { id: 'AM_REVIEWED', label: 'Đã review' },
  { id: 'ACCEPTED', label: 'Đã chốt' },
  { id: 'REJECTED', label: 'Đã từ chối' },
];

export function ForecastReviewPage() {
  const { isMobile, vw } = useViewport();
  const { currentWarehouse } = useScope();
  const { user } = useAuth();
  const adjustments = useAdjustments();
  const customers = useMockTable('customers');
  const calendar = useMockTable('day_calendar');
  const auditLog = useMockTable('audit_log');

  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<ForecastAdjustment | null>(null);
  const [editQty, setEditQty] = useState<number>(0);
  const [detailing, setDetailing] = useState<ForecastAdjustment | null>(null);
  const [decisions, setDecisions] = useState<ReviewDecision[]>([]);

  const customerById = useMemo(
    () => new Map(customers.map((c) => [c.id, c])),
    [customers],
  );
  const calByDate = useMemo(
    () => new Map(calendar.map((c) => [c.date, c])),
    [calendar],
  );

  const inScope = useMemo(() => {
    if (!currentWarehouse) return adjustments;
    return adjustments.filter((a) => a.warehouse_id === currentWarehouse.id);
  }, [adjustments, currentWarehouse]);

  const counts = useMemo(() => {
    const c = {
      all: inScope.length,
      NEW: 0,
      AM_REVIEWED: 0,
      ACCEPTED: 0,
      REJECTED: 0,
    } as Record<StateFilter, number>;
    for (const a of inScope) c[a.state]++;
    return c;
  }, [inScope]);

  const filtered = useMemo(() => {
    if (stateFilter === 'all') return inScope;
    return inScope.filter((a) => a.state === stateFilter);
  }, [inScope, stateFilter]);

  useEffect(() => {
    if (detailing) {
      reviewService.listDecisions(detailing.id).then(setDecisions);
    } else {
      setDecisions([]);
    }
  }, [detailing]);

  const today = toISODate(new Date());

  const totalQty = inScope.reduce((s, a) => s + a.qty, 0);
  const lateCount = inScope.filter((a) => a.is_late).length;
  const accepted = counts.ACCEPTED;
  const acceptedRate = counts.all > 0 ? Math.round((accepted / counts.all) * 100) : 0;

  function toggleAll() {
    const visibleIds = filtered
      .filter((a) => a.state === 'NEW' || a.state === 'AM_REVIEWED')
      .map((a) => a.id);
    if (visibleIds.every((id) => selected.has(id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visibleIds));
    }
  }

  async function bulkAction(action: 'REVIEW' | 'ACCEPT' | 'REJECT') {
    if (!user || selected.size === 0) return;
    const inputs = Array.from(selected).map((id) => ({
      adjustmentId: id,
      action,
      actor: user.email,
    }));
    await reviewService.transitionBulk(inputs);
    setSelected(new Set());
  }

  async function singleAction(adj: ForecastAdjustment, action: 'REVIEW' | 'ACCEPT' | 'REJECT' | 'REOPEN') {
    if (!user) return;
    try {
      await reviewService.transition({
        adjustmentId: adj.id,
        action,
        actor: user.email,
      });
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert((e as Error).message);
    }
  }

  async function saveEdit() {
    if (!editing || !user) return;
    await reviewService.editQty({
      adjustmentId: editing.id,
      newQty: editQty,
      actor: user.email,
    });
    setEditing(null);
  }

  const cols: Column<ForecastAdjustment>[] = [
    {
      header: (
        <input
          type="checkbox"
          onChange={toggleAll}
          checked={
            filtered.length > 0 &&
            filtered
              .filter((a) => a.state === 'NEW' || a.state === 'AM_REVIEWED')
              .every((a) => selected.has(a.id))
          }
          style={{ accentColor: 'var(--bx-primary)' }}
        />
      ),
      width: 40,
      render: (r) => {
        const canSelect = r.state === 'NEW' || r.state === 'AM_REVIEWED';
        if (!canSelect) return null;
        return (
          <input
            type="checkbox"
            checked={selected.has(r.id)}
            onChange={(e) => {
              const next = new Set(selected);
              if (e.target.checked) next.add(r.id);
              else next.delete(r.id);
              setSelected(next);
            }}
            onClick={(e) => e.stopPropagation()}
            style={{ accentColor: 'var(--bx-primary)' }}
          />
        );
      },
    },
    {
      header: 'Khách hàng',
      render: (r) => {
        const c = customerById.get(r.customer_id);
        return (
          <div>
            <div style={{ fontWeight: 600, color: 'var(--bx-text-dark)' }}>
              {c?.name ?? r.customer_id}
            </div>
            <div
              style={{
                fontSize: 10.5,
                color: 'var(--bx-text-muted)',
                fontFamily: 'var(--bx-font-mono)',
              }}
            >
              {c?.code ?? r.customer_id.slice(-6)}
              {c?.tier && <span> · {viLabel(c.tier)}</span>}
            </div>
          </div>
        );
      },
    },
    {
      header: 'Ngày target',
      mono: true,
      width: 110,
      render: (r) => {
        const evt = calByDate.get(r.date)?.event_type ?? 'NORMAL';
        const cutoff = computeCutoff(r.date, evt, today);
        return (
          <div>
            <div style={{ fontWeight: 600, color: 'var(--bx-text-dark)' }}>{r.date}</div>
            <div
              style={{
                fontSize: 10,
                color:
                  cutoff.urgency === 'late'
                    ? 'var(--bx-error)'
                    : cutoff.urgency === 'urgent'
                      ? '#d97706'
                      : 'var(--bx-text-muted)',
                marginTop: 2,
              }}
            >
              {cutoff.lead_days >= 0
                ? `Còn ${cutoff.lead_days} ngày · cut-off ${cutoff.cutoff_days}`
                : `Quá ${-cutoff.lead_days} ngày`}
            </div>
          </div>
        );
      },
    },
    {
      header: 'Sự kiện',
      priority: 2,
      render: (r) => {
        const evt = calByDate.get(r.date)?.event_type ?? 'NORMAL';
        return (
          <Badge
            kind={evt === 'NORMAL' ? 'DRAFT' : evt === 'MEGA_SALE' ? 'CRITICAL' : 'WARNING'}
            size="sm"
          >
            {viLabel(evt)}
          </Badge>
        );
      },
    },
    {
      header: 'Số đơn',
      align: 'right',
      mono: true,
      width: 100,
      render: (r) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (r.state === 'ACCEPTED') return;
            setEditing(r);
            setEditQty(r.qty);
          }}
          disabled={r.state === 'ACCEPTED'}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: r.state === 'ACCEPTED' ? 'default' : 'pointer',
            fontFamily: 'var(--bx-font-mono)',
            fontVariantNumeric: 'tabular-nums',
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--bx-text-dark)',
            padding: '4px 8px',
            borderRadius: 6,
            width: '100%',
            textAlign: 'right',
          }}
        >
          {formatNumber(r.qty)}
        </button>
      ),
    },
    {
      header: 'Trạng thái',
      width: 140,
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Badge
            kind={
              r.state === 'ACCEPTED'
                ? 'APPROVED'
                : r.state === 'REJECTED'
                  ? 'CRITICAL'
                  : r.state === 'AM_REVIEWED'
                    ? 'INFO'
                    : 'DRAFT'
            }
            size="sm"
          >
            {r.state === 'ACCEPTED'
              ? 'Đã chốt'
              : r.state === 'REJECTED'
                ? 'Từ chối'
                : r.state === 'AM_REVIEWED'
                  ? 'Đã review'
                  : 'Chờ duyệt'}
          </Badge>
          {r.is_late && (
            <Badge kind="WARNING" size="sm">
              Trễ
            </Badge>
          )}
        </div>
      ),
    },
    {
      header: '',
      width: 200,
      render: (r) => (
        <div
          style={{
            display: 'flex',
            gap: 4,
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {r.state === 'NEW' && (
            <>
              <Button
                size="sm"
                variant="outline"
                icon="check"
                onClick={() => singleAction(r, 'REVIEW')}
              >
                Review
              </Button>
              <Button
                size="sm"
                variant="ghost"
                icon="x"
                onClick={() => singleAction(r, 'REJECT')}
              >
                Từ chối
              </Button>
            </>
          )}
          {r.state === 'AM_REVIEWED' && (
            <>
              <Button
                size="sm"
                variant="primary"
                icon="check"
                onClick={() => singleAction(r, 'ACCEPT')}
              >
                Chốt
              </Button>
              <Button
                size="sm"
                variant="ghost"
                icon="x"
                onClick={() => singleAction(r, 'REJECT')}
              >
                Từ chối
              </Button>
            </>
          )}
          {r.state === 'REJECTED' && (
            <Button
              size="sm"
              variant="outline"
              icon="refresh"
              onClick={() => singleAction(r, 'REOPEN')}
            >
              Mở lại
            </Button>
          )}
          {r.state === 'ACCEPTED' && (
            <Badge kind="APPROVED" size="sm">
              Khóa
            </Badge>
          )}
          <Button
            size="sm"
            variant="ghost"
            icon="eye"
            onClick={() => setDetailing(r)}
          />
        </div>
      ),
    },
  ];

  const filteredAuditFor = (id: string): AuditLog[] =>
    auditLog.filter(
      (a) =>
        a.entity_id === id ||
        (typeof a.payload?.adjustment_id === 'string' && a.payload.adjustment_id === id),
    );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Lập kế hoạch', 'Dự báo', 'Phê duyệt']}
        title="Phê duyệt điều chỉnh dự báo"
        subtitle={
          currentWarehouse
            ? `${currentWarehouse.code} · ${counts.NEW} chờ duyệt · ${counts.AM_REVIEWED} đã review · ${counts.ACCEPTED} đã chốt`
            : 'Chọn kho ở thanh trên'
        }
        actions={
          selected.size > 0 ? (
            <>
              <span style={{ fontSize: 12, color: 'var(--bx-text-muted)' }}>
                Đã chọn {selected.size}
              </span>
              <Button
                variant="outline"
                size="sm"
                icon="x"
                onClick={() => bulkAction('REJECT')}
              >
                Từ chối
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon="check"
                onClick={() => bulkAction('REVIEW')}
              >
                Review
              </Button>
              <Button
                variant="primary"
                size="sm"
                icon="check"
                onClick={() => bulkAction('ACCEPT')}
              >
                Chốt
              </Button>
            </>
          ) : (
            <Button variant="outline" icon="download" size="sm">
              Export CSV
            </Button>
          )
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <div
          style={{
            display: 'grid',
            gap: 12,
            marginBottom: 16,
            gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          }}
        >
          <Metric
            label="Tổng điều chỉnh"
            value={counts.all}
            sublabel="rows toàn bộ"
          />
          <Metric
            label="Đơn dự kiến"
            value={formatNumber(totalQty)}
            sublabel="đơn (qty)"
          />
          <Metric
            label="Tỉ lệ chốt"
            value={`${acceptedRate}%`}
            sublabel={`${accepted}/${counts.all} ACCEPTED`}
            deltaTone={acceptedRate > 50 ? 'up' : 'flat'}
          />
          <Metric
            label="Trễ hạn"
            value={lateCount}
            sublabel="cut-off violation"
            deltaTone={lateCount > 0 ? 'down' : 'flat'}
          />
        </div>

        <Card padding={0}>
          <div
            style={{
              padding: '12px 14px',
              borderBottom: '1px solid var(--bx-border-muted)',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div
              role="tablist"
              style={{
                display: 'inline-flex',
                gap: 4,
                padding: 4,
                background: 'var(--bx-surface-2)',
                borderRadius: 10,
              }}
            >
              {STATE_TABS.map((t) => {
                const active = stateFilter === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setStateFilter(t.id)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      height: 26,
                      padding: '0 12px',
                      border: 'none',
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: active ? '#fff' : 'transparent',
                      color: active ? 'var(--bx-text-dark)' : 'var(--bx-text-body)',
                      fontWeight: active ? 600 : 500,
                      fontSize: 12.5,
                      boxShadow: active ? 'var(--bx-shadow-pill)' : 'none',
                      fontFamily: 'var(--bx-font-sans)',
                    }}
                  >
                    {t.label}
                    <span
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        fontSize: 11,
                        color: active ? 'var(--bx-primary)' : 'var(--bx-text-muted)',
                      }}
                    >
                      {counts[t.id]}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{ flex: 1 }} />
            <Button variant="ghost" size="sm" icon="filter">
              Bộ lọc khác
            </Button>
          </div>

          <Table
            columns={cols}
            rows={filtered}
            getRowKey={(r) => r.id}
            emptyText={
              counts.all === 0
                ? 'Chưa có điều chỉnh nào — gửi từ trang "Gửi điều chỉnh".'
                : 'Không có điều chỉnh khớp bộ lọc'
            }
          />
        </Card>
      </div>

      {/* Edit qty dialog */}
      <Dialog
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Sửa số đơn dự kiến"
        subtitle={
          editing
            ? `${customerById.get(editing.customer_id)?.name ?? editing.customer_id} · ${editing.date}`
            : ''
        }
        width={420}
        footer={
          <>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Hủy
            </Button>
            <Button variant="primary" icon="check" onClick={saveEdit}>
              Cập nhật
            </Button>
          </>
        }
      >
        {editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Field
              label="Số đơn mới"
              hint={
                editing.state === 'AM_REVIEWED'
                  ? 'Sửa qty sẽ reset trạng thái về "Chờ duyệt"'
                  : 'Số nguyên ≥ 0'
              }
            >
              <Input
                type="number"
                value={editQty}
                onChange={(e) => setEditQty(Math.max(0, Math.floor(Number(e.target.value) || 0)))}
              />
            </Field>
            <div style={{ fontSize: 12, color: 'var(--bx-text-muted)' }}>
              Cũ: <strong style={{ fontFamily: 'var(--bx-font-mono)' }}>{editing.qty}</strong> →
              Mới: <strong style={{ fontFamily: 'var(--bx-font-mono)' }}>{editQty}</strong>
            </div>
          </div>
        )}
      </Dialog>

      {/* Detail dialog with state pipeline + audit timeline */}
      <Dialog
        open={!!detailing}
        onClose={() => setDetailing(null)}
        title={
          detailing
            ? `Điều chỉnh · ${customerById.get(detailing.customer_id)?.name ?? detailing.customer_id}`
            : ''
        }
        subtitle={detailing ? `${detailing.date} · ${formatNumber(detailing.qty)} đơn` : ''}
        width={640}
      >
        {detailing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--bx-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                Pipeline
              </div>
              <StatePipeline state={detailing.state} />
            </div>

            <DetailFields adj={detailing} customer={customerById.get(detailing.customer_id)} />

            <div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--bx-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                Lịch sử quyết định
              </div>
              {decisions.length === 0 ? (
                <div
                  style={{
                    padding: 12,
                    background: 'var(--bx-surface-2)',
                    borderRadius: 8,
                    fontSize: 12.5,
                    color: 'var(--bx-text-muted)',
                  }}
                >
                  Chưa có quyết định nào
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {decisions.map((d) => (
                    <div
                      key={d.id}
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: '8px 12px',
                        background: 'var(--bx-surface-2)',
                        borderRadius: 8,
                        fontSize: 12.5,
                        alignItems: 'center',
                      }}
                    >
                      <Badge size="sm" kind="INFO">
                        {d.action}
                      </Badge>
                      <span style={{ flex: 1 }}>
                        {d.from_state} → <strong>{d.to_state}</strong>
                      </span>
                      <span style={{ color: 'var(--bx-text-muted)', fontSize: 11 }}>
                        {d.decided_by} ·{' '}
                        {new Date(d.decided_at).toLocaleString('vi-VN', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <AuditTimeline entries={filteredAuditFor(detailing.id)} title="Audit log đầy đủ" />
          </div>
        )}
      </Dialog>
    </div>
  );
}

function DetailFields({
  adj,
  customer,
}: {
  adj: ForecastAdjustment;
  customer: Customer | undefined;
}) {
  const rows: { label: string; value: React.ReactNode; mono?: boolean }[] = [
    { label: 'ID', value: adj.id, mono: true },
    {
      label: 'Khách hàng',
      value: customer ? `${customer.name} (${customer.code})` : adj.customer_id,
    },
    { label: 'Ngày target', value: adj.date, mono: true },
    { label: 'Số đơn', value: formatNumber(adj.qty), mono: true },
    { label: 'Nguồn', value: adj.source },
    { label: 'Trễ hạn', value: adj.is_late ? 'Có' : 'Không' },
    { label: 'Người gửi', value: adj.submitted_by },
    {
      label: 'Gửi lúc',
      value: new Date(adj.submitted_at).toLocaleString('vi-VN'),
    },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 10,
        background: 'var(--bx-surface-2)',
        padding: 14,
        borderRadius: 10,
      }}
    >
      {rows.map((r) => (
        <div key={r.label}>
          <div
            style={{
              fontSize: 10.5,
              color: 'var(--bx-text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontWeight: 700,
              marginBottom: 2,
            }}
          >
            {r.label}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--bx-text-dark)',
              fontFamily: r.mono ? 'var(--bx-font-mono)' : 'inherit',
              fontVariantNumeric: r.mono ? 'tabular-nums' : 'normal',
            }}
          >
            {r.value}
          </div>
        </div>
      ))}
    </div>
  );
}
